import { Link } from "@inertiajs/react"
import { Card } from "@/components/ui/card"
import { RowAvatar } from "@/components/domain/row-avatar"
import type { RelatedAccount } from "@/types/appuser"

// Right-rail panel for the appuser detail page. Renders up to 10 other
// appusers in the same tenant who share an email or a device id with the
// current appuser. The presenter tags whichever overlap key it found
// (sharedEmail OR sharedDevice) so we can render an inline reason chip
// next to each name.
//
// Empty state is intentional and explicit — "no related accounts found"
// — rather than hiding the section, so an operator can tell the lookup
// ran but came back clean.

type Props = {
  accounts: RelatedAccount[]
}

function describeShared(account: RelatedAccount): string | null {
  if (account.sharedEmail) {
    return `Shares email ${account.sharedEmail}`
  }
  if (account.sharedDevice) {
    return `Shares device ${account.sharedDevice}`
  }
  return null
}

function displayName(account: RelatedAccount): string {
  return account.combinedName ?? account.email ?? `Appuser #${account.id}`
}

export function AppuserRelatedAccounts({ accounts }: Props) {
  if (accounts.length === 0) {
    return (
      <Card className="space-y-2 p-5">
        <div className="text-sm font-medium text-app-text">Related accounts</div>
        <div className="text-sm text-app-muted">No related accounts found.</div>
      </Card>
    )
  }

  return (
    <Card className="space-y-3 p-5">
      <div className="text-sm font-medium text-app-text">Related accounts</div>
      <ul className="divide-y divide-border">
        {accounts.map((account) => {
          const shared = describeShared(account)
          const name = displayName(account)
          return (
            <li key={account.id} className="py-2 first:pt-0 last:pb-0">
              <Link
                href={`/appusers/${account.id}`}
                className="flex items-center gap-3 rounded-control px-1 py-1 transition hover:bg-white/5"
              >
                <RowAvatar name={name} className="h-9 w-9 text-sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-app-text">{name}</div>
                  {account.email && (
                    <div className="truncate text-xs text-app-muted">{account.email}</div>
                  )}
                  {shared && (
                    <div className="truncate text-xs text-app-subtle">{shared}</div>
                  )}
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
